import { fixed, inches, mm, trim } from './format';
import { requireFinite, requirePositive, require, step, type Solved } from './types';

/** Exact by definition since 1959. Not an approximation. */
export const MM_PER_INCH = 25.4;

export function inchToMm(value: number): Solved<number> {
  requireFinite(value, 'inches');
  const result = value * MM_PER_INCH;
  return {
    value: result,
    steps: [
      step('formula', 'mm = inches × 25.4'),
      step('substitute', `${trim(value)} × 25.4`),
      step('result', `= ${mm(result)}`),
      step(
        'note',
        'Going to millimetres always makes the number bigger. If it got smaller, you divided.',
      ),
    ],
  };
}

export function mmToInch(value: number): Solved<number> {
  requireFinite(value, 'millimetres');
  const result = value / MM_PER_INCH;
  return {
    value: result,
    steps: [
      step('formula', 'inches = mm ÷ 25.4'),
      step('substitute', `${trim(value, 3)} ÷ 25.4`),
      step('result', `= ${inches(result)}`),
      step(
        'note',
        'Going to inches always makes the number smaller. One inch is a hair over 25 mm.',
      ),
    ],
  };
}

export interface Fraction {
  readonly numerator: number;
  readonly denominator: number;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export function simplify({ numerator, denominator }: Fraction): Fraction {
  const g = gcd(Math.abs(numerator), Math.abs(denominator)) || 1;
  return { numerator: numerator / g, denominator: denominator / g };
}

export function fractionToDecimal(numerator: number, denominator: number): Solved<number> {
  requireFinite(numerator, 'numerator');
  requirePositive(denominator, 'denominator');
  const result = numerator / denominator;
  return {
    value: result,
    steps: [
      step('formula', 'A fraction is a division. Top over bottom.'),
      step('substitute', `${numerator} ÷ ${denominator}`),
      step('result', `= ${fixed(result, 4)}`),
      step('note', 'Sixteenths step by .0625 each line. Eighths step by .125.'),
    ],
  };
}

export interface DecimalToFraction {
  readonly fraction: Fraction;
  /** True when the fraction lands exactly on the decimal. */
  readonly exact: boolean;
  /** Signed error of the fraction against the decimal, in inches. */
  readonly error: number;
}

/**
 * Nearest shop fraction to a decimal.
 *
 * Denominators are powers of two only (up to `maxDenominator`, default 64),
 * because that is what a rule and a drill index actually offer. A "nearest
 * fraction" that returned 17/50 would be arithmetically fine and useless.
 */
export function decimalToFraction(value: number, maxDenominator = 64): Solved<DecimalToFraction> {
  requireFinite(value, 'decimal');
  require(
    Number.isInteger(Math.log2(maxDenominator)),
    'maxDenominator must be a power of two',
  );

  const numerator = Math.round(value * maxDenominator);
  const raw: Fraction = { numerator, denominator: maxDenominator };
  const fraction = simplify(raw);
  const asDecimal = fraction.numerator / fraction.denominator;
  const error = asDecimal - value;
  const exact = Math.abs(error) < 1e-9;

  const label = `${fraction.numerator}/${fraction.denominator}`;
  const steps = [
    step('formula', `Multiply by ${maxDenominator} to count how many ${maxDenominator}ths it is.`),
    step('substitute', `${trim(value)} × ${maxDenominator} = ${trim(value * maxDenominator, 2)}`),
    step('compute', `Nearest whole count is ${numerator}, so ${numerator}/${maxDenominator}`),
    step(
      'result',
      fraction.denominator === 1
        ? `= ${fraction.numerator}`
        : `= ${label}${exact ? '' : ` (${trim(asDecimal)}")`}`,
    ),
  ];
  if (!exact) {
    steps.push(
      step('note', `That fraction is off by ${fixed(Math.abs(error), 4)}", so it is not exact.`),
    );
  }

  return { value: { fraction, exact, error }, steps };
}

/** Which of two sizes is larger, with both expressed the same way. */
export function compareSizes(a: number, b: number): Solved<-1 | 0 | 1> {
  requireFinite(a, 'first size');
  requireFinite(b, 'second size');
  const difference = a - b;
  const result = Math.abs(difference) < 1e-9 ? 0 : difference > 0 ? 1 : -1;
  return {
    value: result as -1 | 0 | 1,
    steps: [
      step('formula', 'Put both in decimals before you compare. Never eyeball a fraction.'),
      step('substitute', `${fixed(a, 4)} against ${fixed(b, 4)}`),
      step(
        'result',
        result === 0
          ? 'They are the same size.'
          : `The ${result > 0 ? 'first' : 'second'} is bigger by ${fixed(Math.abs(difference), 4)}"`,
      ),
    ],
  };
}
